import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import React from 'react';
import * as styles from './useMagneticWallSheetContext.module.scss';
import { useOptimisticItemContentRects } from './useOptimisticItemContentRects';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import type { MagneticWallType } from '../editor/editorSlice';
import { CREATION_ID } from '../editor/editorSlice';
import type { EmbedValue } from '../editor/embed/matchEmbed';
import { EmbedMode, embedUrl } from '../editor/embed/matchEmbed';
import { getAttributorFromMagneticWallType } from '../editor/formats/utils/attributors';
import { ALT } from '../editor/image/ImageNode';
import MentionBase from '../editor/mentions/MentionBase';
import { useQuill } from '../editor/QuillContext';
import { findBlotByNode, findQuillByNode } from '../editor/utils/findByNode';
import getExternalBrandLogoProps from '../integrations/getExternalBrandLogoProps';
import { deltaToText } from '../utils/text/deltaText';
import truncate from '../utils/text/truncate';

const truncateStart = (str: string | null, length: number) => {
  if (!str) {
    return str;
  }
  const reverse = (s: string) => _.split(s, '').toReversed().join('');
  return reverse(truncate(reverse(str.slice(-length - 10)), length));
};

interface UseMagneticWallSheetContextProps {
  type: MagneticWallType;
  id: string;
  highlight?: boolean;
}

const useMagneticWallSheetContext = ({
  type,
  id,
  highlight,
}: UseMagneticWallSheetContextProps) => {
  const optimisticItemContentRects = useOptimisticItemContentRects();

  const {
    quill,
    selection: { range },
  } = useQuill();

  if (!quill) {
    return { before: null, highlighted: null, after: null };
  }

  let context = null;

  if (id === CREATION_ID || optimisticItemContentRects.has(id)) {
    if (range) {
      const [line, offset] = quill.getLine(range.index);
      if (line) {
        context = {
          line,
          offset,
          length: range.length,
        };
      }
    }
  } else {
    const Attributor = getAttributorFromMagneticWallType(type);
    const elements = quill.root.querySelectorAll(
      `.${Attributor.keyName}-${id}`,
    );

    if (elements.length && elements[0]) {
      const commentBlot = findBlotByNode(elements[0]);
      const blotQuill =
        commentBlot?.scroll.domNode.parentNode &&
        findQuillByNode(commentBlot.scroll.domNode.parentNode);
      if (blotQuill) {
        const index = blotQuill.getIndex(commentBlot);
        const [line, offset] = blotQuill.getLine(index);
        if (line) {
          context = {
            line,
            offset,
            length: commentBlot.length(),
          };
        }
      }
    }
  }

  if (!context) {
    return { before: null, highlighted: null, after: null };
  }

  const delta = context.line.delta();

  const before = truncateStart(
    deltaToText(delta.slice(0, context.offset).ops),
    100,
  );

  const lastDelta = deltaToText(
    delta.slice(context.offset + context.length).ops,
  );

  const after = lastDelta ? truncate(lastDelta, 100) : null;

  const highlighted = _.compact(
    delta
      .slice(context.offset, context.offset + context.length)
      .map(({ insert, attributes }: Op, i: number): React.ReactNode => {
        /* eslint-disable react/no-array-index-key */
        if (!insert) {
          return null;
        }
        if (typeof insert === 'string') {
          if (!highlight) {
            return <span key={i}>{insert}</span>;
          }
          return (
            <em key={i} className={styles.highlight}>
              {insert}
            </em>
          );
        }
        const { image, video, embed, mention } = insert as {
          image?: unknown;
          video?: unknown;
          embed?: EmbedValue;
          mention?: { id: string; type: string };
        };
        if (typeof image === 'string') {
          return <img key={i} className={styles.image} src={image} alt={ALT} />;
        }
        if (typeof video === 'string') {
          return (
            <MentionBase key={i} type="attachment" content={video} to={video} />
          );
        }
        if (embed) {
          const url = embedUrl(embed, EmbedMode.READONLY);

          if (url) {
            return (
              <MentionBase
                key={i}
                type="external"
                icon={
                  <ExternalBrandLogo
                    inline
                    {...getExternalBrandLogoProps(embed.type)}
                  />
                }
                content={url}
              />
            );
          }
        }
        if (
          mention &&
          typeof attributes?.content === 'string' &&
          attributes.content
        ) {
          return (
            <MentionBase
              key={i}
              type="external"
              icon={
                <ExternalBrandLogo
                  inline
                  {...getExternalBrandLogoProps(mention.type)}
                />
              }
              content={attributes.content}
            />
          );
        }
        return (attributes?.content as string | undefined) ?? null;
        /* eslint-enable react/no-array-index-key */
      }),
  );

  return {
    before,
    after,
    highlighted,
  };
};

export default useMagneticWallSheetContext;
